import {
  arrow,
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react-dom-interactions';
// https://floating-ui.com/docs/react-dom-interactions
import { FC, useRef, useState } from 'react';
import Icon from '../Icons/Icon';
import iconStyles from '../Icons/Icon.module.css';
import styles from './Tooltip.module.css';
import { TooltipProps } from './Tooltip.types';

const Tooltip: FC<TooltipProps> = ({ content, size, position }) => {
  const [open, setOpen] = useState(false);
  const arrowRef = useRef(null);

  const {
    x,
    y,
    reference,
    floating,
    strategy,
    context,
    placement,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
  } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: position,
    whileElementsMounted: autoUpdate,
    middleware: [offset(12), flip(), shift(), arrow({ element: arrowRef })],
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[placement.split('-')[0]];

  return (
    <>
      <Icon
        iconName="HelpCircle"
        ref={reference}
        className={iconStyles.iconButton}
        tabIndex={0}
        title="tooltip"
        size={size}
        {...getReferenceProps()}
      />
      <FloatingPortal>
        {open && (
          <div
            ref={floating}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
              width: 'max-content',
            }}
            className={styles.tooltipContainer}
            {...getFloatingProps()}
          >
            <div
              ref={arrowRef}
              className={styles.tooltipArrow}
              style={{
                left: arrowX != null ? `${arrowX}px` : '',
                top: arrowY != null ? `${arrowY}px` : '',
                right: '',
                bottom: '',
                [staticSide!]: '-4px',
              }}
            />
            {content}
          </div>
        )}
      </FloatingPortal>
    </>
  );
};

export default Tooltip;
